import { type HttpResponse } from '@angular/common/http';
import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import type { AuthSession, AuthUser } from '@core/auth/session.types';
import { AppRuntimeCacheRegistryService } from '@core/cache/app-runtime-cache-registry.service';
import { isAppError } from '@core/error-handling/app-error.types';
import {
  AxelorAuthClient,
  type AxelorAuthClientApi,
} from '@core/http/axelor/clients/axelor-auth.client';
import type { AxelorWsAppInfoResponse } from '@core/http/axelor/contracts/axelor-app-info.types';
import { CsrfTokenService } from '@core/http/csrf-token.service';
import { clearStoredSidebarPinned } from '@core/layout/shell-sidebar-pinned.storage';
import { type Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map, mergeMap, tap } from 'rxjs/operators';

/** Query param used by {@link authGuard} when redirecting to sign-in. */
export const AUTH_RETURN_URL_QUERY_PARAM = 'returnUrl';

const AUTH_SESSION_STORAGE_KEY = 'smart-customs-declaration.auth.session';

interface AxelorLoginJsonBody {
  readonly username?: string;
  readonly password?: string;
}

/**
 * Фасад входа/выхода через {@link AxelorAuthClient} (POST /callback, GET корня, GET ws/app/info, POST /logout).
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  /**
   * Не инжектить {@link AxelorAuthClient} в поле класса: он тянет {@link ApiHttpClient} → `APP_CONFIG`.
   * При `APP_INITIALIZER` (restoreSession) конфиг ещё может быть не загружен.
   */
  private readonly injector = inject(Injector);
  private readonly runtimeCacheRegistry = inject(AppRuntimeCacheRegistryService);
  private readonly csrfToken = inject(CsrfTokenService);
  private readonly sessionState = signal<AuthSession | null>(null);

  public readonly session = this.sessionState.asReadonly();
  public readonly isAuthenticated = computed(() => this.sessionState() !== null);
  public readonly user = computed(() => this.sessionState()?.user ?? null);

  /**
   * Восстанавливает снимок сессии из `sessionStorage`.
   */
  public restoreSession(): Observable<void> {
    return of(void 0).pipe(
      delay(0),
      tap(() => {
        if (typeof sessionStorage === 'undefined') {
          return;
        }
        const raw = sessionStorage.getItem(AUTH_SESSION_STORAGE_KEY);
        if (!raw) {
          return;
        }
        try {
          const parsed = JSON.parse(raw) as unknown;
          const session = parseStoredSession(parsed);
          if (session) {
            this.sessionState.set(session);
          } else {
            sessionStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
          }
        } catch {
          sessionStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
        }
      }),
    );
  }

  /**
   * Вход: POST на `callback`, затем GET корня и `GET ws/app/info` для id / имени / логина в сессии.
   */
  public signInWithCredentials(username: string, password: string): Observable<void> {
    const login = username.trim();
    if (!login || !password) {
      return throwError(() => new Error('Укажите логин и пароль.'));
    }

    /** Drop previous user's CSRF before new session requests; do not clear after login (responses set the new token). */
    this.csrfToken.clear();

    return this.getAxelorAuthClient()
      .login({ username: login, password })
      .pipe(
        mergeMap((response) => {
          const outcome = parseAxelorLoginResponse(response, login);
          if (!outcome.ok) {
            return throwError(() => new Error(outcome.message));
          }
          return this.getAxelorAuthClient()
            .getAppRootAfterLogin()
            .pipe(
              catchError(() => of(void 0)),
              mergeMap(() =>
                this.getAxelorAuthClient()
                  .getWsAppInfo()
                  .pipe(
                    map((info) => mapWsAppInfoToSession(info, outcome.session)),
                    catchError(() => of(outcome.session)),
                  ),
              ),
            );
        }),
        tap((session) => {
          this.runtimeCacheRegistry.clearAll();
          this.sessionState.set(session);
          this.persistSession(session);
        }),
        map(() => void 0),
        catchError((err: unknown) => throwError(() => this.toSignInError(err))),
      );
  }

  /**
   * Завершает сессию на сервере (если возможно), затем очищает локальное состояние.
   */
  public signOut(): Observable<void> {
    return this.getAxelorAuthClient()
      .logout()
      .pipe(
        catchError(() => of(null)),
        mergeMap(() => of(void 0)),
        finalize(() => {
          this.runtimeCacheRegistry.clearAll();
          this.clearClientSession();
        }),
      );
  }

  private getAxelorAuthClient(): AxelorAuthClientApi {
    return this.injector.get(AxelorAuthClient);
  }

  private clearClientSession(): void {
    this.sessionState.set(null);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
    }
    this.csrfToken.clear();
    clearStoredSidebarPinned();
  }

  private persistSession(session: AuthSession): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(session));
  }

  private toSignInError(err: unknown): Error {
    if (isAppError(err)) {
      if (err.kind === 'unauthorized' || err.statusCode === 401) {
        return new Error('Неправильный логин или пароль.');
      }
      return new Error(err.message);
    }
    if (err instanceof Error) {
      return err;
    }
    return new Error('Не удалось выполнить вход.');
  }
}

function mapWsAppInfoToSession(info: AxelorWsAppInfoResponse, fallback: AuthSession): AuthSession {
  const idRaw = info['user.id'];
  const name = info['user.name'];
  const login = info['user.login'];

  const id =
    typeof idRaw === 'number' || typeof idRaw === 'string' ? String(idRaw) : fallback.user.id;
  const displayName =
    typeof name === 'string' && name.trim() !== '' ? name.trim() : fallback.user.displayName;
  const loginStr =
    typeof login === 'string' && login.trim() !== '' ? login.trim() : fallback.user.login;

  return {
    establishedAt: fallback.establishedAt,
    user: {
      id,
      displayName,
      login: loginStr,
    },
  };
}

function parseAxelorLoginResponse(
  response: HttpResponse<string>,
  formUsername: string,
): { ok: true; session: AuthSession } | { ok: false; message: string } {
  if (!(response.status === 200 && response.ok && response.body != null && response.body !== '')) {
    return {
      ok: false,
      message: 'Не удалось выполнить вход. Повторите попытку.',
    };
  }

  const body = response.body.trim();

  let errorParam: string | null = null;
  if (response.url) {
    const queryIndex = response.url.indexOf('?');
    if (queryIndex !== -1) {
      errorParam = new URLSearchParams(response.url.slice(queryIndex + 1)).get('error');
    }
  }

  const isHtmlAuthError = body.includes('<!DOCTYPE html>') && body.includes('Authentication error');

  if (errorParam || isHtmlAuthError) {
    return {
      ok: false,
      message: 'Ошибка аутентификации. Проверьте логин и пароль.',
    };
  }

  let loginUser: AxelorLoginJsonBody | null = null;
  try {
    loginUser = JSON.parse(body) as AxelorLoginJsonBody;
  } catch {
    // не JSON — успех ниже по URL callback (как legacy при нестандартном теле)
  }

  const urlLooksLikeCallback = Boolean(response.url && /\/callback/i.test(response.url));

  if (loginUser?.username && urlLooksLikeCallback) {
    return {
      ok: true,
      session: buildSessionFromUsername(loginUser.username.trim() || formUsername),
    };
  }

  const resolvedUsername = loginUser?.username?.trim() || formUsername;
  if (!resolvedUsername && !urlLooksLikeCallback) {
    return {
      ok: false,
      message: 'Не удалось распознать ответ сервера при входе.',
    };
  }

  return { ok: true, session: buildSessionFromUsername(resolvedUsername || formUsername) };
}

function buildSessionFromUsername(username: string): AuthSession {
  const user: AuthUser = {
    id: username,
    displayName: username,
    login: username,
  };
  return {
    user,
    establishedAt: new Date().toISOString(),
  };
}

function parseStoredSession(value: unknown): AuthSession | null {
  if (!isRecord(value)) {
    return null;
  }
  const user = value['user'];
  const establishedAt = value['establishedAt'];
  if (!isRecord(user) || typeof establishedAt !== 'string') {
    return null;
  }
  const id = user['id'];
  const displayName = user['displayName'];
  if (typeof id !== 'string' || typeof displayName !== 'string') {
    return null;
  }
  const loginRaw = user['login'];
  const login = typeof loginRaw === 'string' && loginRaw.trim() !== '' ? loginRaw : displayName;
  return {
    establishedAt,
    user: {
      id,
      displayName,
      login,
    },
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
